import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { PageHeader, EmptyState, useToast, SearchableSelect, SortTh, useSort, InlineSpinner, TableSkeletonRows } from '../components/UI';

const MODULE_LABELS = {
  students:             'Students',
  intake_master:        'Intake Master',
  course_level_master:  'Course Level Master',
  course_master:        'Course Master',
  module_master:        'Module Master',
  component_master:     'Component Master',
  marks_review:         'Marks Review',
  attendance:           'Attendance',
  academic_performance: 'Academic Performance',
  activities:           'Activities',
  disciplinary:         'Disciplinary',
  alumni:               'Alumni',
  requests:             'Requests',
  receipts:             'Receipts',
  audit_logs:           'Audit Logs',
  reports:              'Reports',
  trainer_directory:    'Trainer Directory',
  trainer_claims_queue: 'Trainer Claims Queue',
  trainer_observations: 'Trainer Observations',
  trainer_schedule:     'Trainer Schedule',
  trainer_fee_rates:    'Trainer Fee Rates',
  trainer_marking_permissions: 'Trainer Marking Permissions',
  survey_links:         'eSurvey Links',
  trainer_assessments:  'Trainer Assessments',
  trainer_claims:       'Trainer Claims',
  finance_claims:       'Finance Claims',
};

const ACTION_LABELS = { view: 'View', create: 'Create', update: 'Update', delete: 'Delete' };
const ALL_ACTIONS = ['view', 'create', 'update', 'delete'];
const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', staff: 'Staff', trainer: 'Trainer', finance: 'Finance' };

export default function PermissionsPage() {
  const toast = useToast();
  const [users,      setUsers]      = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [perms,      setPerms]      = useState({});
  const [modules,    setModules]    = useState({});
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    api.get('/users').then(r => {
      setUsers(r.data.data.filter(u => u.role !== 'super_admin' && u.is_active));
    }).catch(() => toast('Failed to load users', 'error'));
  }, []);

  const loadPerms = useCallback(async (uid) => {
    if (!uid) return;
    setLoading(true);
    try {
      const res = await api.get(`/permissions/${uid}`);
      setPerms(res.data.data.permissions);
      setModules(res.data.data.modules);
    } catch { toast('Failed to load permissions', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPerms(selectedId); }, [selectedId]);

  const toggle = (mod, action) => {
    setPerms(p => ({
      ...p,
      [mod]: { ...p[mod], [action]: !p[mod]?.[action] }
    }));
  };

  // Tick all for a row (module)
  const toggleRow = (mod) => {
    const actions = modules[mod] || [];
    const allOn = actions.every(a => perms[mod]?.[a]);
    setPerms(p => ({
      ...p,
      [mod]: Object.fromEntries(actions.map(a => [a, !allOn]))
    }));
  };

  // Tick all for a column (action)
  const toggleCol = (action) => {
    const mods = Object.keys(modules).filter(m => modules[m].includes(action));
    const allOn = mods.every(m => perms[m]?.[action]);
    setPerms(p => {
      const next = { ...p };
      mods.forEach(m => { next[m] = { ...next[m], [action]: !allOn }; });
      return next;
    });
  };

  // Tick all everything
  const toggleAll = () => {
    const allOn = Object.entries(modules).every(([mod, actions]) =>
      actions.every(a => perms[mod]?.[a])
    );
    setPerms(() => {
      const next = {};
      for (const [mod, actions] of Object.entries(modules)) {
        next[mod] = Object.fromEntries(actions.map(a => [a, !allOn]));
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.put(`/permissions/${selectedId}`, { permissions: perms });
      toast('Permissions saved', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const allChecked = Object.entries(modules).every(([mod, actions]) =>
    actions.every(a => perms[mod]?.[a])
  );

  const moduleRows = useMemo(() =>
    Object.entries(modules).map(([mod, actions]) => ({ mod, actions, label: MODULE_LABELS[mod] || mod })),
    [modules]
  );
  const { sorted: sortedModules, sortKey: modSortKey, sortDir: modSortDir, toggleSort: toggleModSort } = useSort(moduleRows);

  return (
    <>
      <PageHeader title="User Permissions" subtitle="Set per-user access for each module" />

      {/* User selector */}
      <div className="sms-card mb-4">
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--sms-txt2)', display: 'block', marginBottom: 8 }}>
          Select staff
        </label>
        <div className="d-flex gap-3 flex-wrap align-items-center">
          <div style={{ maxWidth: 320, flex: '1 1 320px' }}>
            <SearchableSelect value={selectedId} onChange={e => setSelectedId(e.target.value)}
              placeholder="-- Select staff --"
              options={users.map(u => ({ value: u.id, label: u.full_name, right: ROLE_LABELS[u.role] || u.role }))} />
          </div>
          {selectedId && (
            <span style={{ fontSize: 12, color: 'var(--sms-txt3)' }}>
              <i className="bi bi-info-circle me-1" />
              Super Admin always has full access and cannot be configured here.
            </span>
          )}
        </div>
      </div>

      {/* Permission matrix */}
      {!selectedId && (
        <EmptyState icon="bi-person-gear" message="Select a user above to configure their permissions" />
      )}

      {selectedId && (loading || Object.keys(modules).length > 0) && (
        <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--sms-bdr)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <h6 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--sms-txt)' }}>
              Permission Matrix
            </h6>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', color: 'var(--sms-txt2)', userSelect: 'none' }}>
                <input type="checkbox" className="perm-check" checked={allChecked} onChange={toggleAll} />
                Tick all
              </label>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="perm-table">
              <thead>
                <tr>
                  <SortTh label="Module" field="label" sortKey={modSortKey} sortDir={modSortDir} onSort={toggleModSort} style={{ textAlign: 'left' }} />
                  {ALL_ACTIONS.map(a => (
                    <th key={a}>
                      <div className="perm-col-all">
                        {ACTION_LABELS[a]}
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', marginTop: 4 }}>
                          <input type="checkbox" className="perm-check"
                            style={{ width: 14, height: 14 }}
                            checked={Object.keys(modules)
                              .filter(m => modules[m].includes(a))
                              .every(m => perms[m]?.[a])}
                            onChange={() => toggleCol(a)} />
                          <span>All</span>
                        </label>
                      </div>
                    </th>
                  ))}
                  <th>
                    <div className="perm-col-all">Row all<span> </span></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? <TableSkeletonRows columns={ALL_ACTIONS.length + 2} /> : sortedModules.map(({ mod, actions }) => {
                  const rowAll = actions.every(a => perms[mod]?.[a]);
                  return (
                    <tr key={mod}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <i className={`bi ${MOD_ICON[mod] || 'bi-circle'}`}
                             style={{ color: 'var(--sms-txt2)', fontSize: 15 }} />
                          {MODULE_LABELS[mod] || mod}
                        </div>
                      </td>
                      {ALL_ACTIONS.map(a => (
                        <td key={a}>
                          {actions.includes(a) ? (
                            <input type="checkbox" className="perm-check"
                              checked={!!(perms[mod]?.[a])}
                              onChange={() => toggle(mod, a)}
                              aria-label={`${MODULE_LABELS[mod]} ${ACTION_LABELS[a]}`} />
                          ) : (
                            <span style={{ color: 'var(--sms-txt3)', fontSize: 16 }}>—</span>
                          )}
                        </td>
                      ))}
                      <td>
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, cursor: 'pointer', fontSize: 12, color: 'var(--sms-txt2)' }}>
                          <input type="checkbox" className="perm-check"
                            style={{ width: 14, height: 14 }}
                            checked={rowAll}
                            onChange={() => toggleRow(mod)} />
                          All
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--sms-bdr)', display: 'flex', justifyContent: 'flex-end' }}>
            <button className="sms-btn sms-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><InlineSpinner style={{ marginRight: 4 }} />Saving…</> : <><i className="bi bi-check-lg" />Save permissions</>}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const MOD_ICON = {
  students:             'bi-people',
  intake_master:        'bi-table',
  course_level_master:  'bi-bar-chart-steps',
  course_master:        'bi-journal-bookmark',
  module_master:        'bi-collection',
  component_master:     'bi-diagram-3',
  marks_review:         'bi-clipboard2-check',
  attendance:           'bi-calendar-check',
  academic_performance: 'bi-graph-up',
  activities:           'bi-activity',
  disciplinary:         'bi-exclamation-circle',
  alumni:               'bi-award',
  requests:             'bi-envelope-open',
  receipts:             'bi-receipt',
  audit_logs:           'bi-shield-check',
  reports:              'bi-file-earmark-bar-graph',
  trainer_directory:    'bi-people',
  trainer_claims_queue: 'bi-receipt',
  trainer_observations: 'bi-clipboard-check',
  trainer_schedule:     'bi-calendar3',
  trainer_fee_rates:    'bi-currency-dollar',
  trainer_marking_permissions: 'bi-person-check',
  trainer_assessments:  'bi-journal-check',
  trainer_claims:       'bi-cash-coin',
  finance_claims:       'bi-wallet2',
};
